import React from 'react';
import {FormattedMessage} from 'react-intl';

// Mode de calcul d'une majoration pour une course à cheval sur la plage (enum SurchargeMode côté
// back, logique dans SurchargeWindow.java). OVERLAP = comportement historique, défaut des
// majorations existantes.
export const SURCHARGE_MODES = ['OVERLAP', 'PICKUP', 'PRORATA'];
export const DEFAULT_SURCHARGE_MODE = 'OVERLAP';

// Course d'exemple commune aux trois cartes : elle démarre 20 min avant le début de la plage et
// dure 30 min, donc 10 min (un tiers) tombent dans la plage. Mêmes proportions dans le schéma.
const EXAMPLE_BEFORE_MIN = 20;
const EXAMPLE_DURATION_MIN = 30;
const EXAMPLE_BASE_FARE = 60;
const EXAMPLE_WEIGHT = {OVERLAP: 1, PICKUP: 0, PRORATA: (EXAMPLE_DURATION_MIN - EXAMPLE_BEFORE_MIN) / EXAMPLE_DURATION_MIN};

const toMinutes = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) {
    return null;
  }
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const toHhmm = (minutes) => {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

const toNumber = (value, fallback) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Prix de la course d'exemple, même formule que ModuleService.calculatePrice. */
const examplePrice = (mode, multiplier, increaseAmount) => {
  const w = EXAMPLE_WEIGHT[mode];
  return EXAMPLE_BASE_FARE + w * (EXAMPLE_BASE_FARE * (multiplier - 1) + increaseAmount);
};

/**
 * Schéma d'une course (barre) face à la plage (zone teintée à droite du repère « début »).
 * Échelle : 0-100 ; la course va de 10 à 70, la plage commence à 50 (20 min avant / 10 min après).
 */
// En HTML (spans positionnés en %) plutôt qu'en SVG étiré : le texte et les arrondis restent nets
// quelle que soit la largeur. Des spans, car le schéma vit dans un <button>.
const Timeline = ({mode, startLabel}) => {
  const rideFrom = 10;
  const windowFrom = 50;
  const rideTo = 70;
  const surcharged = {OVERLAP: [rideFrom, rideTo], PICKUP: null, PRORATA: [windowFrom, rideTo]}[mode];
  return (
    <span className="cc-surcharge-timeline" aria-hidden="true">
      <span className="cc-tl-window" style={{left: `${windowFrom}%`}}/>
      <span className="cc-tl-ride" style={{left: `${rideFrom}%`, width: `${rideTo - rideFrom}%`}}/>
      {surcharged && (
        <span className="cc-tl-surcharged" style={{left: `${surcharged[0]}%`, width: `${surcharged[1] - surcharged[0]}%`}}/>
      )}
      <span className="cc-tl-label" style={{left: `${windowFrom}%`}}>{startLabel}</span>
    </span>
  );
};

/**
 * Choix du mode de calcul, en cartes radio. Chaque carte explique le mode, montre la même course
 * d'exemple (partie majorée en couleur) et le prix obtenu avec le multiplicateur / montant saisis,
 * pour que la différence entre les trois se lise sans rien lire d'autre.
 */
export default function SurchargeModePicker({value, onChange, startTime, multiplier, increaseAmount, formatter}) {
  const current = value || DEFAULT_SURCHARGE_MODE;
  const start = toMinutes(startTime);
  const startLabel = start == null ? '22:00' : toHhmm(start);
  const rideFrom = toHhmm((start == null ? 22 * 60 : start) - EXAMPLE_BEFORE_MIN);
  const rideTo = toHhmm((start == null ? 22 * 60 : start) - EXAMPLE_BEFORE_MIN + EXAMPLE_DURATION_MIN);
  const m = toNumber(multiplier, 1);
  const inc = toNumber(increaseAmount, 0);
  const format = (n) => (formatter ? formatter.format(n) : n.toFixed(2));

  const onKeyDown = (e) => {
    const delta = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    if (delta !== 0) {
      e.preventDefault();
      const i = SURCHARGE_MODES.indexOf(current);
      onChange(SURCHARGE_MODES[(i + delta + SURCHARGE_MODES.length) % SURCHARGE_MODES.length]);
    }
  };

  return (
    <div className="cc-surcharge-modes">
      <p className="cc-surcharge-modes-help">
        <FormattedMessage id="SURCHARGE_MODE_HELP"/>
      </p>
      <p className="cc-surcharge-modes-example">
        <FormattedMessage id="SURCHARGE_MODE_EXAMPLE" values={{
          from: rideFrom, to: rideTo, start: startLabel, base: format(EXAMPLE_BASE_FARE),
          b: (chunks) => <strong>{chunks}</strong>,
        }}/>
      </p>
      <div role="radiogroup" className="cc-surcharge-mode-list" onKeyDown={onKeyDown}>
        {SURCHARGE_MODES.map(mode => {
          const selected = current === mode;
          return (
            <button key={mode} type="button" role="radio" aria-checked={selected} tabIndex={selected ? 0 : -1}
                    className={`cc-surcharge-mode ${selected ? 'is-selected' : ''}`}
                    onClick={() => onChange(mode)}>
              <span className="cc-surcharge-mode-radio" aria-hidden="true"/>
              <span className="cc-surcharge-mode-body">
                <span className="cc-surcharge-mode-title"><FormattedMessage id={`SURCHARGE_MODE_${mode}`}/></span>
                <span className="cc-surcharge-mode-desc"><FormattedMessage id={`SURCHARGE_MODE_${mode}_DESC`}/></span>
                <span className="cc-surcharge-mode-demo">
                  <Timeline mode={mode} startLabel={startLabel}/>
                  <span className="cc-surcharge-mode-price">
                    <FormattedMessage id={`SURCHARGE_MODE_${mode}_RESULT`}/>
                    <strong>{format(examplePrice(mode, m, inc))}</strong>
                  </span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Explication des jours cochés quand la plage passe minuit : la lecture diffère selon le mode
 * (calendaire pour OVERLAP, rattachée au jour de début pour PICKUP / PRORATA).
 */
export function OvernightDaysHint({mode, startTime, endTime}) {
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  if (start == null || end == null || start <= end) {
    return null;
  }
  const anchored = (mode || DEFAULT_SURCHARGE_MODE) !== 'OVERLAP';
  return (
    <p className="cc-surcharge-days-hint">
      <FormattedMessage id={anchored ? 'SURCHARGE_OVERNIGHT_ANCHORED' : 'SURCHARGE_OVERNIGHT_CALENDAR'}
                        values={{start: toHhmm(start), end: toHhmm(end), b: (chunks) => <strong>{chunks}</strong>}}/>
    </p>
  );
}
